import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/**
 * Who currently has unsaved input.
 *
 * This exists for one rule: **the update prompt must never reload over a
 * half-filled check-in.** Losing what someone typed because a deploy landed
 * while they were mid-entry teaches them the app cannot be trusted with the
 * thing it exists to do, and no amount of correctness elsewhere recovers that.
 *
 * A count of claimants rather than a boolean, because two editors can be open
 * at once — a sheet over a form — and the second one closing must not clear a
 * flag the first still needs.
 */
interface UnsavedWorkApi {
  readonly hasUnsavedWork: boolean
  /** Called by an editor while it holds unsaved changes. */
  readonly claim: () => () => void
}

const UnsavedWorkContext = createContext<UnsavedWorkApi | null>(null)

export function UnsavedWorkProvider({ children }: { readonly children: ReactNode }) {
  const [count, setCount] = useState(0)

  const claim = useCallback(() => {
    setCount((n) => n + 1)
    let released = false
    return () => {
      // Guarded: a release running twice — React 19 strict mode double-invokes
      // effect cleanups — would decrement below zero and report the app clean
      // while an editor is still dirty.
      if (released) {
        return
      }
      released = true
      setCount((n) => Math.max(0, n - 1))
    }
  }, [])

  const api = useMemo(() => ({ hasUnsavedWork: count > 0, claim }), [count, claim])

  return <UnsavedWorkContext.Provider value={api}>{children}</UnsavedWorkContext.Provider>
}

export function useUnsavedWork(): UnsavedWorkApi {
  const api = useContext(UnsavedWorkContext)
  if (!api) {
    throw new Error('useUnsavedWork must be used inside an <UnsavedWorkProvider>')
  }
  return api
}

/**
 * Declares that this component holds unsaved changes while `dirty` is true.
 *
 * Editors call this rather than touching the context, so registering and
 * releasing cannot get out of step — the effect's cleanup is the release.
 */
export function useDeclareUnsaved(dirty: boolean): void {
  const { claim } = useUnsavedWork()
  const release = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!dirty) {
      return
    }
    release.current = claim()
    return () => {
      release.current?.()
      release.current = null
    }
  }, [dirty, claim])
}
